import { ApiClient } from '@api/api-configuration';
import { BillingEntityTypeEnum, type BalanceItem } from '@api/READ_ONLY/billing_api/Api';
import {
  DEFAULT_LIST_INITIAL_STATE,
  DEFAULT_LIST_STORAGE,
  LIST_TABLE_APPEARANCE,
} from '@app/common/listTableAppearance';
import { DEFAULT_CURRENCY } from '@app/common/constants';
import { getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import { AppDataTable } from '@app/components/common/AppDataTable';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CaregiverTransactionsSubTable from './components/CaregiverTransactionsSubTable/CaregiverTransactionsSubTable';
import { getCaregiverCashTableColumns } from './components/CaregiverCashTable/caregiverCashTableColumns.config';
import RecordDepositModal from './RecordDepositModal';
import { DEFAULT_CAREGIVER_CASH_SORT, type CaregiverCashRow } from './types';

const CaregiverCash: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = getNumberFormattingLocaleByLanguage(i18n.language);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [depositRow, setDepositRow] = React.useState<CaregiverCashRow | null>(null);

  const fetchCaregiverCash = useCallback(
    async (params: { limit: number; offset: number; query?: string; sort?: string; [key: string]: unknown }) => {
      const { limit, offset, query, sort } = params;
      const response = await ApiClient.billingApi.billing.listBalances({
        type: BillingEntityTypeEnum.CaregiverCashRegister,
        ...(query ? { search: query } : {}),
        sort: sort || DEFAULT_CAREGIVER_CASH_SORT,
        offset,
        limit,
      });
      return {
        data: response.data.data,
        total: response.data.total || response.data.data.length,
      };
    },
    []
  );

  const tableConfig = React.useMemo(
    () => ({
      tableId: 'caregiverCash',
      initialState: DEFAULT_LIST_INITIAL_STATE,
      storage: DEFAULT_LIST_STORAGE,
      urlParamConfig: { external: { searchTerm: 'search' } },
      fetchData: fetchCaregiverCash,
      transformData: (item: BalanceItem): CaregiverCashRow => ({
        id: item.billingAccountId,
        caregiverId: item.entityId,
        name: item.entityName,
        balance: item.balance ?? 0,
        currency: DEFAULT_CURRENCY,
        createdAt: item.createdAt,
      }),
      rowKey: 'id' as const,
      columns: getCaregiverCashTableColumns(t, locale, {
        onRecordDeposit: (row: CaregiverCashRow) => setDepositRow(row),
      }),
      searchPlaceholder: t('caregiver-cash-search-placeholder'),
      searchConfig: {
        minSearchLength: 3,
        debounceMs: 300,
      },
      expandable: {
        // Match the Service Requests list: a narrow, left-pinned expand toggle
        // (defaults to 44px otherwise, which reads as an oversized empty column).
        expandColumnWidth: 35,
        expandColumnSticky: 'left' as const,
        expandedRowRender: (row: CaregiverCashRow) => <CaregiverTransactionsSubTable caregiverId={row.caregiverId} />,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, locale, fetchCaregiverCash]
  );

  return (
    <>
      <AppDataTable appearance={LIST_TABLE_APPEARANCE} key={reloadKey} config={tableConfig} />

      <RecordDepositModal
        open={depositRow !== null}
        caregiverId={depositRow?.caregiverId ?? null}
        caregiverName={depositRow?.name}
        outstandingAmount={depositRow?.balance}
        onClose={() => setDepositRow(null)}
        // Remount the list (and any open sub-table) so both reflect the new deposit.
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </>
  );
};

export default CaregiverCash;
